import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useStoreon } from "storeon/react";
import "./blog.scss";
import { Card as PostThumb } from "../../components/wrappers/card";
import { SearchWrapper } from "../../components/blog/search/search";
import { Carrousel } from "../../components/blog/carrousel/carrousel";
import Post from "../../components/post/post";
import { InfiniteScrollList } from "../../components/infiniteScrollList/infiniteScrollList";
import {
  BLOG_GET_POSTS,
  BLOG_ADD_PAGE,
  BLOG_RESET_PAGE,
  BLOG_SET_POSTS,
  BLOG_SET_SEARCH_KEYWORD,
} from "../../core/store/blog";
import { CATEGORIES_GET } from "../../core/store/category";
import chevronDown from "../../assets/img/chevron-down-orange-2.svg";
import useMedia from "../../core/hooks/use-media";

const FAVORITE_CATEGORY = {
  label: "Favourites",
  active: false,
  hidden: false,
  params: { _articleFavorites: true },
};

export default function Blog() {
  const {
    blog: { isLoading, isCompleted, posts, searchKeyword, config },
    dispatch,
  } = useStoreon("blog");
  const {
    auth: {
      profile: { id },
    },
  } = useStoreon("auth");
  const {
    category: { categories },
  } = useStoreon("category");
  const [menuItems, setMenuItems] = useState(null);
  const [currentPostId, setCurrentPostId] = useState(null);
  const [showMenuOptions, setShowMenuOptions] = useState(false);
  const [isCleanSearch, setIsCleanSearch] = useState(false);
  const [hasSearchValue, setHasSearchValue] = useState(false);

  const { blogId } = useParams();

  const categoriesRef = useRef(null);

  const media = useMedia(
    ["(max-width: 900px)", "(min-width: 900px)"],
    ["mobile", "desktop"],
    "desktop"
  );

  const history = useHistory();

  const handleMenuItemSelect = (index, flag = null) => {
    dispatch(BLOG_RESET_PAGE);

    const currentKeyword = document.getElementById("search__input")?.value;

    const keywordParam =
      currentKeyword && hasSearchValue
        ? { _articleKeyword: currentKeyword }
        : {};

    fetchPosts({
      ...menuItems[index].params,
      _articleUserId: id,
      ...keywordParam,
    });

    setMenuItems(
      menuItems?.map((v, i) => ({ ...v, active: Boolean(index === i) }))
    );

    setShowMenuOptions(false);

    window.scroll(0, categoriesRef.current?.offsetTop || 0);
    setTimeout(() => {
      !flag && hasCurrentPostId() && handleOnBlogDetailsBack();
      setCurrentPostId(flag);
    }, 10);
  };

  const fetchPosts = (params) => dispatch(BLOG_GET_POSTS, params);

  useEffect(() => {
    dispatch(BLOG_RESET_PAGE);
  }, []);

  useEffect(() => {
    if (categories.length) {
      const tmp = categories.sort((a, b) => (a.order <= b.order ? -1 : 1));

      if (!menuItems) {
        id
          ? setMenuItems([
              ...tmp.map((v, i) => ({
                ...v,
                params: { _articleSection: v.label },
                hidden: false,
                active: i === 0,
              })),
              FAVORITE_CATEGORY,
            ])
          : setMenuItems(
              tmp.map((v, i) => ({
                ...v,
                params: { _articleSection: v.label },
                hidden: false,
                active: i === 0,
              }))
            );

        const defaultParams = {
          _articleSection:
            tmp.filter((v) => v.active)[0]?.label || tmp[0].label,
        };

        !blogId && fetchPosts({ ...defaultParams, _articleUserId: id });
      }
    } else {
      dispatch(CATEGORIES_GET);
    }
  }, [categories, posts.length]);

  useEffect(() => {
    const param = (blogId && parseInt(blogId.split("-")[0])) || null;
    hasCurrentPostId() !== param && setCurrentPostId(param);

    if (categoriesRef.current === null) {
      return;
    }

    hasCurrentPostId()
      ? window.scroll(0, 0)
      : window.scroll(0, categoriesRef?.current?.offsetTop);
  }, [currentPostId, blogId]);

  useEffect(() => {
    if (!hasCurrentPostId() && hasSearchValue) {
      handleMenuItemSelect(findActiveItemIndex());
    }

    if (!searchKeyword && hasSearchValue) {
      refreshActiveCategory();
    }
  }, [currentPostId]);

  useEffect(() => {
    if (!searchKeyword && hasSearchValue) {
      refreshActiveCategory();
      setHasSearchValue(false);
    }
  }, [searchKeyword]);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);
  const handleReadMore = (id, slug) => {
    history.push(`/blog/${id}-${slug}`);
  };

  const handleLoadMore = () => {
    const params = menuItems?.filter((v) => v.active)[0]?.params;
    if (!isCompleted && posts.length > 0) {
      dispatch(BLOG_ADD_PAGE);
      fetchPosts({ ...params, _articleUserId: id });
    }
  };

  const findActiveItemIndex = () => {
    let index = 0;
    menuItems.map((v, i) => {
      index = v.active ? i : index;
      v.active = false;
    });

    return index;
  };

  const refreshActiveCategory = () => {
    handleMenuItemSelect(findActiveItemIndex());
  };

  const handleCloseBtnClick = () => {
    setIsCleanSearch(true);
  };

  const isItemFavoriteActive = () => {
    return Object.keys(menuItems.filter((v) => v.active)[0].params).some(
      (v) => v === "_articleFavorites"
    );
  };

  const onRefreshItems = (itemId, favoriteId) => {
    isItemFavoriteActive()
      ? dispatch(
          BLOG_SET_POSTS,
          posts.filter((v) => v.id !== itemId)
        )
      : dispatch(
          BLOG_SET_POSTS,
          posts.map(
            (v) => (v.id === itemId && { ...v, favorite: favoriteId }) || v
          )
        );
  };

  const getPost = (post, index) => {
    return (
      <PostThumb
        key={`${post?.id || index}`}
        id={"post"}
        elementType={"article"}
        labels={{
          like: config?.like_label,
          share: config?.share_label,
          successCopied: config?.successfully_shared_label,
          readMore: config?.read_more_label,
        }}
        item={post}
        onReadMore={handleReadMore}
        coverImage={true}
        onRefreshItems={onRefreshItems}
      />
    );
  };

  const handleOnSearch = (keyword) => {
    const params = menuItems?.filter((v) => v.active)[0]?.params;
    dispatch(BLOG_RESET_PAGE);
    dispatch(BLOG_SET_SEARCH_KEYWORD, keyword);

    fetchPosts({ ...params, _articleUserId: id, _articleKeyword: keyword });
  };

  const handleOnShowMenu = () => setShowMenuOptions(!showMenuOptions);

  const handleOnBlogDetailsBack = () => {
    history.push("/blog");
  };

  const handleResetCleanSearch = () => {
    setIsCleanSearch(false);
    dispatch(BLOG_SET_SEARCH_KEYWORD, "");
  };

  const onSelectOtherCategoryBlog = (label) => {
    let index = -1;
    menuItems?.map((v, i) => (index = v.id === label ? i : index));
    index !== -1 && handleMenuItemSelect(index, label);
  };

  const hasCurrentPostId = () =>
    currentPostId !== undefined && currentPostId !== null;

  const MenuSkeleton = () => (
    <>
      <div
        data-testid="skeleton_menu"
        className="blog___left_menu_content skeleton_blog___left_menu_content"
      >
        <div></div>
      </div>
    </>
  );

  return (
    <>
      <div className={`${!hasCurrentPostId() ? "blog__background" : ""}`}>
        <div
          className={`blog__wrapper ${
            hasCurrentPostId() ? "blog__selected__wrapper" : ""
          }`}
        >
          {!hasCurrentPostId() && (
            <Carrousel
              isLoaded={Boolean(config?.carousel_section)}
              image={
                config?.carousel_section?.image?.formats?.large?.url ||
                config?.carousel_section?.image?.formats?.medium?.url ||
                config?.carousel_section?.image?.formats?.small?.url ||
                config?.carousel_section?.image?.formats?.thumbnail?.url ||
                config?.carousel_section?.thumbnail_video?.url ||
                config?.carousel_section?.image?.url
              }
              title={config?.carousel_section?.title}
              description={config?.carousel_section?.body}
              id={config?.carousel_section?.id}
              slug={config?.carousel_section?.slug}
              buttonLabel={config?.carrousel_button_detail}
              onReadMore={handleReadMore}
            />
          )}
          {/*  SEARCH */}
          {!hasCurrentPostId() && (
            <div id="search" ref={categoriesRef}>
              <SearchWrapper
                header={config?.search_label}
                placeholder={
                  media === "desktop"
                    ? config?.search_placeholder
                    : config?.search_label
                }
                keyword={searchKeyword}
                handleOnSearch={handleOnSearch}
                handleResetCleanSearch={handleResetCleanSearch}
                isCleanSearch={isCleanSearch}
                hasSearchValue={hasSearchValue}
                onSetHasSearchValue={setHasSearchValue}
              />
            </div>
          )}
          <div className="blog___content">
            {/*  LEFT MENU */}
            {config ? (
              <div
                data-testid="left_menu_content"
                className="blog___left_menu_content"
              >
                <div
                  className="blog___left_menu_header"
                  onClick={handleOnShowMenu}
                >
                  <h3>{config?.category_label}</h3>
                  <img src={chevronDown} />
                </div>
                <div className={showMenuOptions ? "" : "blog___hidden"}>
                  <div className="divider"></div>
                  <div className="blog___left_menu_items">
                    {menuItems?.map(
                      (item, i) =>
                        !item.hidden && (
                          <div
                            data-testid={`menu_item_${i}`}
                            key={i}
                            className={
                              item.active
                                ? "blog__left_menu_item_selected blog__left_menu_item"
                                : "blog__left_menu_item"
                            }
                            onClick={() => handleMenuItemSelect(i)}
                          >
                            <span>{item.label}</span>
                          </div>
                        )
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <MenuSkeleton />
            )}
            {/*  CONTENT */}
            {!hasCurrentPostId() ? (
              <div data-testid="blog__list" className="blog___list">
                {/*LIST*/}
                {posts?.length || isLoading ? (
                  <InfiniteScrollList
                    items={posts}
                    itemsWrapperClass={"blog__list"}
                    loading={isLoading}
                    canLoadMore={
                      !(isCompleted || isLoading || hasCurrentPostId())
                    }
                    loadMoreCallBack={handleLoadMore}
                    renderItemCallBack={getPost}
                  />
                ) : (
                  <div className="blog___list_not_content">
                    <h3>{config?.search_not_item_label}</h3>
                    <span>{config?.search_not_item_description}</span>
                    <a className="Button Small" onClick={handleCloseBtnClick}>
                      {config?.search_not_item_button_label}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <Post
                postId={currentPostId}
                onBack={handleOnBlogDetailsBack}
                onRefreshItems={onRefreshItems}
                onSelectOtherCategoryBlog={onSelectOtherCategoryBlog}
                // onFindPostById={onFindPostById}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
